import { Injectable } from '@angular/core';
import { Http, QueryBus, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { deserializeDocumentBuyerModel, deserializeDocumentClientModel, serializeDocumentBuyerModel, serializeDocumentClientModel, serializeDocumentExchangeRateModel, serializeDocumentPaymentListItemModel } from '@skylab-shared/business';
import { HttpService, ObservableCollection, QueryBus as QueryBusBase, Repository, RepositoryBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { CalculationMethod, DocumentPaymentListItemModel, ExchangeRateModel, PurchaseBankAccountModel, PurchaseGetDocumentDTO, PurchaseInvoiceDocumentItemModel, PurchaseInvoiceDocumentModel, PurchaseUpdateDocumentDTO, RelatedEntity, VatTableItemModel } from '@skylab-shared/dataModel';
import { getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ClientRepository, ClientRepositoryBase } from '../client/client.repo';
import { ProductRepository } from '../product/product.repo';
import { PurchaseInvoiceDocumentBO } from './purchase-invoice-document.bo';

@Repository({
    boType: PurchaseInvoiceDocumentBO,
    apiController: TaggedUrl.join('purchase', [Tags.Api]),
    dto: {
        get: PurchaseGetDocumentDTO,
        update: PurchaseUpdateDocumentDTO
    }
})
@Injectable({ providedIn: 'root'})
export class PurchaseInvoiceDocumentRepository extends RepositoryBase<PurchaseInvoiceDocumentBO, PurchaseInvoiceDocumentModel, PurchaseGetDocumentDTO, PurchaseUpdateDocumentDTO> {
    public constructor(
        http: Http,
        private readonly weaver: Weaver,
        private readonly queryBus: QueryBus,
        private readonly productRepository: ProductRepository,
        private readonly clientRepository: ClientRepository
    ) {
        super(http as HttpService);
    }

    protected createBO(): PurchaseInvoiceDocumentBO {
        return new PurchaseInvoiceDocumentBO(this.weaver as WeaverBase, this.queryBus as any as QueryBusBase, this.productRepository, this.clientRepository as ClientRepositoryBase);
    }

    protected async fromPlainObjectAsync(item: PurchaseGetDocumentDTO): Promise<PurchaseInvoiceDocumentModel> {
        return new PurchaseInvoiceDocumentModel(pickBy<Partial<PurchaseInvoiceDocumentModel>>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,
            favourite: item.favourite,

            ksefSubmissionMode: item.ksefSubmissionMode,
            ksefHash: item.ksefHash,
            ksefCollectiveIdentifier: item.ksefCollectiveIdentifier,
            ksefHeaderId: item.ksefHeaderId,
            ksefDocumentId: item.ksefDocumentId,
            dateOfAssigningKsefNumber: item.dateOfAssigningKsefNumber,

            automaticVatCalculation: item.automaticVatCalculation,

            ddkState: item.ddkState,
            eppState: item.eppState,

            relatedInventoryDocuments: item.relatedInventoryDocuments?.map(doc => new RelatedEntity(doc.id, doc.version)) || [],
            includesInventoryControl: item.includesInventoryControl,
            inventoryEffect: item.inventoryEffect,

            calculationMethod: item.calculationMethod,
            costCalculationMethod: item.costCalculationMethod,
            cashBasis: item.cashBasis,
            seller: deserializeDocumentClientModel(item.seller),
            supplier: deserializeDocumentBuyerModel(item.supplier),
            bankAccount: item.sellerBankAccount
                ? new PurchaseBankAccountModel(pickBy<PurchaseBankAccountModel>({
                    bankName: item.sellerBankAccount.bankName,
                    number: item.sellerBankAccount.number,
                    status: item.sellerBankAccount.whiteListStatus,
                    statusDate: item.sellerBankAccount.whiteListLastDateCheck,
                    statusId: item.sellerBankAccount.whiteListCheckingId
                }, val => val !== void 0))
                : null,
            documentNumber: item.documentNumber,
            exchangeRate: item.exchangeRate ? new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                manual: item.exchangeRate.manual,
                units: item.exchangeRate.units,
                rate: item.exchangeRate.rate,
                tableNo: item.exchangeRate.tableNo,
            }, val => val !== void 0)) : null,
            expenseName: item.expenseName,
            externalDocumentNumber: item.externalDocumentNumber,
            kind: item.kind,
            note: item.note,
            numberingId: item.numberingId,
            splitPayment: item.splitPayment,
            jpkVatGroups: item.jpkVatDocumentGroups,

            grossValue: item.totalBaseCurrencyGrossValue,
            currencyGrossValue: item.exchangeRate ? item.totalGrossValue : null,

            netValue: item.totalBaseCurrencyNetValue,

            fixedAsset: item.fixedAsset,
            issuedByVatPayer: item.issuedByVatPayer,
            transactionLocation: item.transactionLocation,

            deliveryDate: item.deliveryDate,
            issueDate: item.issueDate,
            receiptDate: item.receiptDate,
            vatDeductionDate: item.vatDeductionDate,

            dueDate: item.dueDate ? new Date(item.dueDate) : null,
            payments: new ObservableCollection(item.payments.map(item => {
                return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
                    id: item.id,
                    date: item.date,
                    value: item.amount,
                    onDocument: item.onDocument
                }, val => val !== void 0));
            })),
            recipient: item.recipient,
            issuer: item.issuer,

            communityTransactionId: item.communityTransactionId,


            currencyId: item.currencyId,
            paymentMethodId: item.paymentMethodId,
            items: new ObservableCollection(item.items.map(documentItem => {
                return new PurchaseInvoiceDocumentItemModel(pickBy<Partial<PurchaseInvoiceDocumentItemModel>>({
                    ordinalNumber: documentItem.no,
                    discountKind: documentItem.discountKind,
                    discountValue: documentItem.discountValue,
                    jpkVatGroups: documentItem.jpkVatProductGroup ? [documentItem.jpkVatProductGroup] : [],
                    note: documentItem.note,
                    price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                    price_grossBeforeDiscount: documentItem.unitGrossPrice,
                    price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                    id: documentItem.id,
                    price_netBeforeDiscount: documentItem.unitNetPrice,
                    productId: documentItem.productId,
                    includesInventoryControl: documentItem.includesInventoryControl,
                    relatedInventoryItemsIds: documentItem.relatedInventoryItemsIds || [],
                    productName: documentItem.productName,
                    pkwiu: documentItem.pkwiu,
                    cnCode: documentItem.cnCode,
                    quantity: documentItem.quantity,
                    unitId: documentItem.unitId,
                    symbol: documentItem.symbol,
                    barCode: documentItem.barCode,
                    value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                    value_grossBeforeDiscount: documentItem.grossValue,
                    value_netAfterDiscount: documentItem.netValueAfterDiscount,
                    value_netBeforeDiscount: documentItem.netValue,
                    value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                    value_vatBeforeDiscount: documentItem.vatValue,
                    vatRateId: documentItem.vatRateId,
                    inventoryTotalCost: documentItem.inventoryTotalCost,
                    inventoryUnitCost: documentItem.inventoryUnitCost
                }, val => val !== void 0));
            })),
            vatTableItems: new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.gross,
                    netValue: item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            }))
        }, val => val !== void 0));
    }

    protected async preparePlainObjectAsync(model: PurchaseInvoiceDocumentModel): Promise<PurchaseUpdateDocumentDTO> {
        return {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            version: model.version,
            favourite: model.favourite,

            relatedInventoryDocuments: model.relatedInventoryDocuments?.map(doc => ({
                id: doc.id,
                version: doc.version
            })) || [],
            inventoryEffect: model.inventoryEffect,

            recipient: model.recipient,
            issuer: model.issuer,

            ksefDocumentId: model.ksefDocumentId,
            ksefSubmissionMode: model.ksefSubmissionMode,
            originalEInvoiceDocumentId: model.originalEInvoiceDocumentId,

            automaticVatCalculation: model.automaticVatCalculation,

            calculationMethod: model.calculationMethod,
            costCalculationMethod: model.costCalculationMethod,
            cashBasis: model.cashBasis,
            seller: serializeDocumentClientModel(model.seller),
            supplier: serializeDocumentBuyerModel(model.supplier),
            sellerBankAccount: model.bankAccount
                ? {
                    bankName: model.bankAccount.bankName,
                    number: model.bankAccount.number,
                    whiteListStatus: model.bankAccount.status,
                    whiteListLastDateCheck: model.bankAccount.statusDate,
                    whiteListCheckingId: model.bankAccount.statusId
                }
                : null,
            documentNumber: model.documentNumber,
            expenseName: model.expenseName,
            externalDocumentNumber: model.externalDocumentNumber,
            kind: model.kind,
            note: model.note,
            numberingId: model.numberingId,
            splitPayment: model.splitPayment,
            jpkVatDocumentGroups: model.jpkVatGroups,

            fixedAsset: model.fixedAsset,

            issueDate: model.issueDate ? getStartOfDate(model.issueDate) : null,
            receiptDate: model.receiptDate ?  getStartOfDate(model.receiptDate) : null,
            deliveryDate: getStartOfDate(model.deliveryDate),
            vatDeductionDate: getStartOfDate(model.vatDeductionDate),
            dueDate: getStartOfDate(model.dueDate!),
            payments: model.payments.map(serializeDocumentPaymentListItemModel),

            transactionLocation: model.transactionLocation,

            communityTransactionId: model.communityTransactionId,
            items: model.items.map(elm => ({
                discountValue: elm.discountValue,
                discountKind: elm.discountKind,
                no: elm.ordinalNumber,
                note: elm.note,
                productId: elm.productId,
                productName: elm.productName,
                pkwiu: elm.pkwiu,
                cnCode: elm.cnCode,
                jpkVatProductGroup: elm.jpkVatGroup,
                quantity: elm.quantity,
                symbol: elm.symbol,
                barCode: elm.barCode,
                includesInventoryControl: elm.includesInventoryControl,
                relatedInventoryItemsIds: elm.relatedInventoryItemsIds || [],
                unitId: elm.unitId,
                price: model.calculationMethod === CalculationMethod.Gross ? elm.price_grossBeforeDiscount : elm.price_netBeforeDiscount,
                vatRateId: elm.vatRateId,
                reducedPriceGross: elm.price_grossAfterDiscount,
                priceGross: elm.price_grossBeforeDiscount,
                reducedPriceNet: elm.price_netAfterDiscount,
                priceNet: elm.price_netBeforeDiscount,
                reducedValueGross: elm.value_grossAfterDiscount,
                valueGross: elm.value_grossBeforeDiscount,
                reducedValueNet: elm.value_netAfterDiscount,
                valueNet: elm.value_netBeforeDiscount,
                valueVat: elm.value_vatBeforeDiscount,
                reducedValueVat: elm.value_vatAfterDiscount
            })),
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null,
            currencyId: model.currencyId,
            paymentMethodId: model.paymentMethodId,
            vatTable: {
                items: model.vatTableItems.map(item => {
                    return {
                        gross: item.grossValue,
                        net: item.netValue,
                        vat: item.vatValue,
                        vatRateId: item.vatRateId
                    };
                })
            },
        };
    }
}
